import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import MainLayout from "@/components/layouts";
import { useAppSelector } from "@/lib/store";
import { stripePromise } from "@/lib/stripe";
import { usePayerProfile, useVendorPaymentDues } from "./payment-dues.hooks";
import { useAutopayStates } from "./payment-dues-autopay.hooks";
import UnifiedPaymentDuesTable from "./components/UnifiedPaymentDuesTable";
import ExtraPaymentModal from "./components/ExtraPaymentModal";
import PayerProfilePanel from "./components/PayerProfilePanel";
import { Button } from "@/components/ui/button";
import StripePaymentModal from "@/components/stripe/StripePaymentModal";
import PaymentMethodSelectionDialog from "@/components/stripe/PaymentMethodSelectionDialog";
import { Plus } from "lucide-react";
import {
  createStripePaymentForIndividualDue,
  createStripePaymentForSubsDue,
  createStripePaymentForExtraPayment,
  finalizeStripePaymentForIndividualDue,
  finalizeStripePaymentForSubsDue,
  finalizeStripePaymentForExtraPayment,
  getIndividualPaymentDueReport,
  getSubsPaymentDueReport,
  markIndividualPaymentDuesAsPaid,
  markSubsPaymentDuesAsPaid,
  payDueWithSavedMethod,
  updatePaymentTerms,
} from "./payment-dues.api";
import { formatPayerMethodSummary } from "@/services/stripe-payer-profile.service";
import { IndividualPaymentDue, PaymentDueReport, PaymentDuesFilters, PaymentScope, SubsPaymentDue } from "./payment-dues.entity";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import moment from "moment";
import type { CreatePaymentSheetResponse, StripePaymentMethodType } from "@/services/stripe-subs-payments.service";
import {
  showStripeDueFinalizeToast,
  showStripeExtraFinalizeToast,
} from "@/app/payment-dues/stripe-finalize-toast";

type StripePaymentContext = {
  type: "individual" | "subs";
  dueId: string;
  fromDate?: string | null;
  toDate?: string | null;
  paymentScope: PaymentScope;
};

type StripeModalState = {
  isOpen: boolean;
  context: StripePaymentContext | { type: "extra"; extraPaymentData: ExtraPaymentData } | null;
  paymentIntentId: string | null;
  clientSecret: string | null;
  amount: number;
  currency: string | null;
  applicationFeeAmount: number;
  netTransferAmount: number;
  paymentMethodType: StripePaymentMethodType | null;
};

type ExtraPaymentData = {
  recipientType: "individual" | "subs";
  recipientId: string;
  amount: number;
  description: string;
};

type PendingStripePayment =
  | { type: "individual"; due: IndividualPaymentDue; paymentScope: PaymentScope }
  | { type: "subs"; due: SubsPaymentDue; paymentScope: PaymentScope }
  | { type: "extra"; extraPaymentData: ExtraPaymentData };

type DateRange = { from?: Date; to?: Date };

export default function PaymentDuesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate initial state from URL
  const [searchText, setSearchText] = useState(() => searchParams.get("q") ?? "");
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const from = searchParams.get("from");
    const to = searchParams.get("to");
    return {
      from: from ? moment(from, "YYYY-MM-DD").toDate() : undefined,
      to: to ? moment(to, "YYYY-MM-DD").toDate() : undefined,
    };
  });

  // Keep URL in sync with filter state
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchText.trim()) params.set("q", searchText.trim());
    if (dateRange.from) params.set("from", moment(dateRange.from).format("YYYY-MM-DD"));
    if (dateRange.to) params.set("to", moment(dateRange.to).format("YYYY-MM-DD"));
    setSearchParams(params, { replace: true });
  }, [searchText, dateRange.from, dateRange.to, setSearchParams]);

  const activeFilters: PaymentDuesFilters = useMemo(
    () => ({
      searchText: searchText || undefined,
      fromDate: dateRange.from ? moment(dateRange.from).format("YYYY-MM-DD") : undefined,
      toDate: dateRange.to ? moment(dateRange.to).format("YYYY-MM-DD") : undefined,
    }),
    [searchText, dateRange.from, dateRange.to]
  );

  const {
    individualPaymentDues,
    subsPaymentDues,
    loading: duesLoading,
    error: duesError,
    refresh: refreshDues,
  } = useVendorPaymentDues(activeFilters);

  const vendorOrgId = useAppSelector((state) => state.organization.activeOrgId);

  const {
    profile: payerProfile,
    loading: payerProfileLoading,
    error: payerProfileError,
    refresh: refreshPayerProfile,
    setProfile: setPayerProfile,
  } = usePayerProfile();

  const {
    states: autopayStates,
    loading: autopayLoading,
    error: autopayError,
    refresh: refreshAutopay,
    applyState: applyAutopayState,
  } = useAutopayStates();

  const [markingKey, setMarkingKey] = useState<string | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [stripePayingKey, setStripePayingKey] = useState<string | null>(null);
  const [paymentTermsSavingKey, setPaymentTermsSavingKey] = useState<string | null>(null);
  const [creatingStripePayment, setCreatingStripePayment] = useState(false);
  const [extraPaymentModalOpen, setExtraPaymentModalOpen] = useState(false);
  const [processingExtraPayment, setProcessingExtraPayment] = useState(false);
  const initialStripeModalState: StripeModalState = {
    isOpen: false,
    context: null,
    paymentIntentId: null,
    clientSecret: null,
    amount: 0,
    currency: "usd",
    applicationFeeAmount: 0,
    netTransferAmount: 0,
    paymentMethodType: null,
  };
  const [stripeModalState, setStripeModalState] = useState<StripeModalState>(initialStripeModalState);
  const [pendingStripePayment, setPendingStripePayment] = useState<PendingStripePayment | null>(null);

  const { individualPaymentDues: filteredIndividualPaymentDues, subsPaymentDues: filteredSubsPaymentDues } = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) {
      return { individualPaymentDues, subsPaymentDues };
    }
    return {
      individualPaymentDues: individualPaymentDues.filter((d) => d.name.toLowerCase().includes(normalizedSearch)),
      subsPaymentDues: subsPaymentDues.filter((d) => d.name.toLowerCase().includes(normalizedSearch)),
    };
  }, [individualPaymentDues, subsPaymentDues, searchText]);

  const closeStripeModal = () => {
    setStripeModalState(initialStripeModalState);
    setStripePayingKey(null);
  };

  const closePaymentMethodDialog = () => {
    setPendingStripePayment(null);
    setStripePayingKey(null);
    setCreatingStripePayment(false);
    setProcessingExtraPayment(false);
  };

  const getAmountForScope = (due: IndividualPaymentDue | SubsPaymentDue, scope: PaymentScope): number => {
    if (scope === "appointments") return due.remaining_appointment_amount ?? 0;
    if (scope === "daily_work") return due.remaining_daily_work_amount ?? 0;
    return due.remaining_amount;
  };

  const getPendingPaymentAmount = () => {
    if (!pendingStripePayment) return 0;
    if (pendingStripePayment.type === "extra") {
      return Math.round(pendingStripePayment.extraPaymentData.amount * 100);
    }
    return Math.round(getAmountForScope(pendingStripePayment.due, pendingStripePayment.paymentScope) * 100);
  };

  const openStripeModal = (
    response: CreatePaymentSheetResponse,
    context: StripeModalState["context"]
  ) => {
    setStripeModalState({
      isOpen: true,
      context,
      paymentIntentId: response.paymentIntentId,
      clientSecret: response.clientSecret,
      amount: response.amount,
      currency: response.currency,
      applicationFeeAmount: response.applicationFeeAmount,
      netTransferAmount: response.netTransferAmount,
      paymentMethodType: response.paymentMethodType,
    });
  };

  const handleIndividualStripePayment = async (due: IndividualPaymentDue, scope: PaymentScope = "both") => {
    const key = `individual:${due.id}`;
    setStripePayingKey(key);
    try {
      if (!due.ready_for_payouts) {
        toast.error("This individual is not ready for payouts yet.");
        setStripePayingKey((current) => (current === key ? null : current));
        return;
      }

      const scopedAmount = getAmountForScope(due, scope);
      const amountInCents = Math.round(scopedAmount * 100);

      if (!Number.isFinite(amountInCents) || amountInCents <= 0) {
        toast.error("Unable to initiate Stripe payment: invalid amount.");
        setStripePayingKey((current) => (current === key ? null : current));
        return;
      }

      setPendingStripePayment({ type: "individual", due, paymentScope: scope });
    } catch (error: any) {
      const message = error?.message ?? "Failed to initiate Stripe payment.";
      toast.error(message);
      console.error(error);
      setStripePayingKey((current) => (current === key ? null : current));
    }
  };

  const handleRefresh = () => {
    void refreshDues();
    void refreshAutopay();
  };

  const sanitizeFileName = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const buildReportFile = (report: PaymentDueReport, filename: string) => {
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
    const header = ["Type", "Particulars", "Detail Link", "Amount"];
    const rows = report.items.map((item) => {
      const typeLabel = item.type === "appointment" ? "Appointment" : "Daily Work";
      const identifier = item.type === "appointment" ? item.id : item.parentId ?? item.id;
      const detailLink = baseUrl ? `${baseUrl}${item.detailPath}` : item.detailPath;
      return [typeLabel, item.label, detailLink, item.amount.toFixed(2)];
    });
    const totalRow = ["", "", "Total", report.totalAmount.toFixed(2)];
    const content = [header, ...rows, totalRow].map((line) => line.join("\t")).join("\n");

    const blob = new Blob([content], { type: "text/tab-separated-values;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleIndividualMarkAsPaid = async (due: IndividualPaymentDue, scope: PaymentScope = "both") => {
    const key = `individual:${due.id}`;
    setMarkingKey(key);
    try {
      await markIndividualPaymentDuesAsPaid(due.id, {
        fromDate: activeFilters.fromDate,
        toDate: activeFilters.toDate,
      }, scope);
      toast.success(`Marked dues for ${due.name} as paid for the selected date range.`);
      await refreshDues();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to mark individual payment dues as paid.");
    } finally {
      setMarkingKey((current) => (current === key ? null : current));
    }
  };

  const handleSubsMarkAsPaid = async (due: SubsPaymentDue, scope: PaymentScope = "both") => {
    const key = `subs:${due.id}`;
    setMarkingKey(key);
    try {
      await markSubsPaymentDuesAsPaid(due.id, {
        fromDate: activeFilters.fromDate,
        toDate: activeFilters.toDate,
      }, scope);
      toast.success(`Marked dues for ${due.name} as paid for the selected date range.`);
      await refreshDues();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to mark subcontractor payment dues as paid.");
    } finally {
      setMarkingKey((current) => (current === key ? null : current));
    }
  };

  const handleIndividualReportDownload = async (due: IndividualPaymentDue) => {
    const key = `individual:${due.id}`;
    setDownloadingKey(key);
    try {
      const report = await getIndividualPaymentDueReport(due.id, {
        fromDate: activeFilters.fromDate,
        toDate: activeFilters.toDate,
      });
      if (report.items.length === 0) {
        toast.info(`No outstanding payments to report for ${due.name}.`);
      } else {
        const slug = sanitizeFileName(due.name || "individual");
        const datePart = new Date().toISOString().split("T")[0];
        const filename = `individual-payment-report-${slug}-${datePart}.tsv`;
        buildReportFile(report, filename);
        toast.success("Individual payment report downloaded.");
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to download individual payment report.");
    } finally {
      setDownloadingKey((current) => (current === key ? null : current));
    }
  };

  const handleSubsReportDownload = async (due: SubsPaymentDue) => {
    const key = `subs:${due.id}`;
    setDownloadingKey(key);
    try {
      const report = await getSubsPaymentDueReport(due.id, {
        fromDate: activeFilters.fromDate,
        toDate: activeFilters.toDate,
      });
      if (report.items.length === 0) {
        toast.info(`No outstanding payments to report for ${due.name}.`);
      } else {
        const slug = sanitizeFileName(due.name || "subs");
        const datePart = new Date().toISOString().split("T")[0];
        const filename = `subs-payment-report-${slug}-${datePart}.tsv`;
        buildReportFile(report, filename);
        toast.success("Sub contractor payment report downloaded.");
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to download sub contractor payment report.");
    } finally {
      setDownloadingKey((current) => (current === key ? null : current));
    }
  };

  const handleSubsStripePayment = async (due: SubsPaymentDue, scope: PaymentScope = "both") => {
    const key = `subs:${due.id}`;
    setStripePayingKey(key);
    try {
      if (!due.ready_for_payouts) {
        toast.error("This subcontractor is not ready for payouts yet.");
        setStripePayingKey((current) => (current === key ? null : current));
        return;
      }

      const scopedAmount = getAmountForScope(due, scope);
      const amountInCents = Math.round(scopedAmount * 100);

      if (!Number.isFinite(amountInCents) || amountInCents <= 0) {
        toast.error("Unable to initiate Stripe payment: invalid amount.");
        setStripePayingKey((current) => (current === key ? null : current));
        return;
      }

      setPendingStripePayment({ type: "subs", due, paymentScope: scope });
    } catch (error: any) {
      const message = error?.message ?? "Failed to initiate Stripe payment.";
      toast.error(message);
      console.error(error);
      setStripePayingKey((current) => (current === key ? null : current));
    }
  };

  const handlePaymentMethodConfirm = async (paymentMethodType: StripePaymentMethodType) => {
    if (!pendingStripePayment) return;

    try {
      setCreatingStripePayment(true);
      if (pendingStripePayment.type === "extra") {
        const { extraPaymentData } = pendingStripePayment;
        const amountInCents = Math.round(extraPaymentData.amount * 100);

        if (!Number.isFinite(amountInCents) || amountInCents <= 0) {
          toast.error("Unable to initiate Stripe payment: invalid amount.");
          closePaymentMethodDialog();
          return;
        }

        setProcessingExtraPayment(true);
        const response = await createStripePaymentForExtraPayment({
          amount: amountInCents,
          description: extraPaymentData.description,
          recipientType: extraPaymentData.recipientType,
          recipientId: extraPaymentData.recipientId,
          paymentMethodType,
          kind: "extra",
        });

        if (!response.clientSecret) {
          toast.error(
            "Stripe payment intent was created without a client secret. Please finish the payment from the Stripe dashboard."
          );
          closePaymentMethodDialog();
          return;
        }

        openStripeModal(response, {
          type: "extra",
          extraPaymentData,
        });
        setPendingStripePayment(null);
        toast.success("Stripe payment intent created. Complete the payment below.");
        return;
      }

      const { due, paymentScope } = pendingStripePayment;
      const amountInCents = Math.round(getAmountForScope(due, paymentScope) * 100);
      const recipientType = pendingStripePayment.type;
      const sheetPayload = {
        amount: amountInCents,
        recipientType,
        recipientId: due.id,
        paymentMethodType,
        kind: "due" as const,
        paymentScope,
        dateRange: {
          fromDate: activeFilters.fromDate ?? undefined,
          toDate: activeFilters.toDate ?? undefined,
        },
      };
      const response =
        recipientType === "individual"
          ? await createStripePaymentForIndividualDue(sheetPayload)
          : await createStripePaymentForSubsDue(sheetPayload);

      if (!response.clientSecret) {
        toast.error(
          "Stripe payment intent was created without a client secret. Please finish the payment from the Stripe dashboard."
        );
        closePaymentMethodDialog();
        return;
      }

      openStripeModal(response, {
        type: recipientType,
        dueId: due.id,
        fromDate: activeFilters.fromDate ?? null,
        toDate: activeFilters.toDate ?? null,
        paymentScope,
      });
      setPendingStripePayment(null);
      setStripePayingKey(null);
      toast.success("Stripe payment intent created. Complete the payment below.");
    } catch (error: any) {
      const message = error?.message ?? "Failed to initiate Stripe payment.";
      toast.error(message);
      console.error(error);
      closePaymentMethodDialog();
    } finally {
      setCreatingStripePayment(false);
      setProcessingExtraPayment(false);
    }
  };

  const savedMethodOption =
    pendingStripePayment &&
    pendingStripePayment.type !== "extra" &&
    !payerProfileLoading &&
    payerProfile?.status === "active" &&
    payerProfile.payer === vendorOrgId &&
    payerProfile.paymentMethodType &&
    payerProfile.last4
      ? {
          label: formatPayerMethodSummary(payerProfile),
          paymentMethodType: payerProfile.paymentMethodType,
        }
      : null;

  const handleSavedMethodConfirm = async () => {
    if (!pendingStripePayment || pendingStripePayment.type === "extra") return;

    const { due, paymentScope } = pendingStripePayment;
    const recipientType = pendingStripePayment.type;
    const amountInCents = Math.round(getAmountForScope(due, paymentScope) * 100);
    const dateRange = {
      fromDate: activeFilters.fromDate ?? undefined,
      toDate: activeFilters.toDate ?? undefined,
    };

    try {
      setCreatingStripePayment(true);
      const response = await payDueWithSavedMethod({
        amount: amountInCents,
        recipientType,
        recipientId: due.id,
        paymentScope,
        dateRange,
      });

      if (response.requiresAction) {
        // Never finalize an unconfirmed intent: bail out unless the
        // additional confirmation step can actually be completed.
        if (!response.clientSecret) {
          toast.error("This payment needs additional confirmation, but none was provided. Please retry or use a one-time method.");
          return;
        }
        const stripe = await stripePromise;
        if (!stripe) {
          toast.error("This payment needs additional confirmation, but Stripe is not configured in this environment.");
          return;
        }
        const nextAction = await stripe.handleNextAction({ clientSecret: response.clientSecret });
        if (nextAction.error) {
          toast.error(nextAction.error.message ?? "The saved payment method could not be confirmed.");
          return;
        }
      }

      const result =
        recipientType === "subs"
          ? await finalizeStripePaymentForSubsDue(due.id, response.paymentIntentId, dateRange, paymentScope)
          : await finalizeStripePaymentForIndividualDue(due.id, response.paymentIntentId, dateRange, paymentScope);
      showStripeDueFinalizeToast(result.status);
      if (result.status === "paid") {
        await refreshDues();
      }
      closePaymentMethodDialog();
    } catch (error: any) {
      // Keep the dialog open so the user can retry or fall back to a one-time method.
      toast.error(error?.message ?? "Failed to pay with the saved payment method.");
      console.error(error);
    } finally {
      setCreatingStripePayment(false);
    }
  };

  const handleStripePaymentSuccess = async (paymentIntentId: string) => {
    if (!stripeModalState.context) {
      closeStripeModal();
      return;
    }

    // Handle extra payment flow
    if (stripeModalState.context.type === "extra") {
      const { extraPaymentData } = stripeModalState.context;
      try {
        setProcessingExtraPayment(true);
        const result = await finalizeStripePaymentForExtraPayment({
          recipientType: extraPaymentData.recipientType,
          recipientId: extraPaymentData.recipientId,
          amount: extraPaymentData.amount,
          description: extraPaymentData.description,
          paymentIntentId,
        });
        showStripeExtraFinalizeToast(result.status);
        if (result.status === "paid") {
          await handleRefresh();
        }
      } catch (error: any) {
        const message = error?.message ?? "Unable to confirm extra payment at this time.";
        toast.error(message);
        console.error(error);
      } finally {
        setProcessingExtraPayment(false);
        closeStripeModal();
      }
      return;
    }

    // Handle regular payment dues flow
    const { type, dueId, fromDate: contextFromDate, toDate: contextToDate, paymentScope } = stripeModalState.context;

    try {
      setStripePayingKey(`${type}:${dueId}`);
      if (type === "subs") {
        const result = await finalizeStripePaymentForSubsDue(dueId, paymentIntentId, {
          fromDate: contextFromDate ?? undefined,
          toDate: contextToDate ?? undefined,
        }, paymentScope);
        showStripeDueFinalizeToast(result.status);
        if (result.status === "paid") {
          await refreshDues();
        }
      } else {
        const result = await finalizeStripePaymentForIndividualDue(dueId, paymentIntentId, {
          fromDate: contextFromDate ?? undefined,
          toDate: contextToDate ?? undefined,
        }, paymentScope);
        showStripeDueFinalizeToast(result.status);
        if (result.status === "paid") {
          await refreshDues();
        }
      }
    } catch (error: any) {
      const message = error?.message ?? "Unable to confirm Stripe payment at this time.";
      toast.error(message);
      console.error(error);
    } finally {
      closeStripeModal();
    }
  };

  // Unified handlers for the new UnifiedPaymentDuesTable
  const handleUnifiedStripePayment = (due: IndividualPaymentDue | SubsPaymentDue, type: "individual" | "subs", scope: PaymentScope) => {
    if (type === "individual") {
      handleIndividualStripePayment(due as IndividualPaymentDue, scope);
    } else {
      handleSubsStripePayment(due as SubsPaymentDue, scope);
    }
  };

  const handleUnifiedMarkAsPaid = (due: IndividualPaymentDue | SubsPaymentDue, type: "individual" | "subs", scope: PaymentScope) => {
    if (type === "individual") {
      handleIndividualMarkAsPaid(due as IndividualPaymentDue, scope);
    } else {
      handleSubsMarkAsPaid(due as SubsPaymentDue, scope);
    }
  };

  const handleUnifiedReportDownload = (due: IndividualPaymentDue | SubsPaymentDue, type: "individual" | "subs") => {
    if (type === "individual") {
      handleIndividualReportDownload(due as IndividualPaymentDue);
    } else {
      handleSubsReportDownload(due as SubsPaymentDue);
    }
  };

  const handleUpdatePaymentTerms = async (
    due: IndividualPaymentDue | SubsPaymentDue,
    type: "individual" | "subs",
    paymentTermsDays: number
  ) => {
    if (due.payment_terms_days === paymentTermsDays) return;

    const key = `${type}:${due.id}`;
    setPaymentTermsSavingKey(key);
    try {
      await updatePaymentTerms(type, due.id, paymentTermsDays);
      toast.success(`Payment terms updated to Net ${paymentTermsDays}.`);
      await refreshDues();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to update payment terms.");
      throw error;
    } finally {
      setPaymentTermsSavingKey((current) => (current === key ? null : current));
    }
  };

  const handleExtraPaymentConfirm = async (data: {
    recipientType: "individual" | "subs";
    recipientId: string;
    amount: number;
    description: string;
  }) => {
    const amountInCents = Math.round(data.amount * 100);

    if (!Number.isFinite(amountInCents) || amountInCents <= 0) {
      toast.error("Unable to initiate Stripe payment: invalid amount.");
      return;
    }

    setExtraPaymentModalOpen(false);
    setPendingStripePayment({ type: "extra", extraPaymentData: data });
  };

  return (
    <MainLayout
      breadcrumb={[
        { name: "Dashboard", href: "/" },
        { name: "Payment Dues", href: "#" },
      ]}
      pageHeader={{
        title: "Payment Dues",
        subtitle: "Track remaining payments for individuals and sub contractors",
        buttonText: "Refresh",
        onButtonClick: handleRefresh,
        additionalButtons: (
          <Button size="lg" variant="outline" onClick={() => setExtraPaymentModalOpen(true)} className="uppercase">
            <Plus className="h-4 w-4 mr-2" />
            Send Extra Payment
          </Button>
        ),
      }}
    >
      <div className="container mx-auto py-6 space-y-6">
        <PayerProfilePanel
          profile={payerProfile}
          loading={payerProfileLoading}
          loadError={payerProfileError}
          onRefresh={refreshPayerProfile}
          onProfileChange={setPayerProfile}
        />

        {/* Shared Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="w-full md:w-1/3">
            <Input
              placeholder="Search individual or subcontractor..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal w-[280px]",
                    !dateRange.from && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {moment(dateRange.from).format("MMM DD, YYYY")} –{" "}
                        {moment(dateRange.to).format("MMM DD, YYYY")}
                      </>
                    ) : (
                      moment(dateRange.from).format("MMM DD, YYYY")
                    )
                  ) : (
                    "Select date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from}
                  selected={dateRange.from ? dateRange as { from: Date; to?: Date } : undefined}
                  onSelect={(range) => setDateRange(range ?? {})}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
            {dateRange.from && (
              <Button variant="ghost" size="icon" onClick={() => setDateRange({})}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              setSearchText("");
              setDateRange({});
              handleRefresh();
            }}
          >
            Clear Filters
          </Button>
        </div>
        {duesError && (
          <div className="bg-destructive/10 text-destructive p-4 rounded-md">
            <p className="font-medium">Error loading payment dues</p>
            <p className="text-sm mt-1">{duesError}</p>
          </div>
        )}

        {autopayError && (
          <div className="bg-destructive/10 text-destructive p-4 rounded-md">
            <p className="font-medium">Error loading autopay settings</p>
            <p className="text-sm mt-1">
              {autopayError} Autopay badges and toggles may be out of date — refresh to retry.
            </p>
          </div>
        )}

        <UnifiedPaymentDuesTable
          individuals={filteredIndividualPaymentDues}
          subs={filteredSubsPaymentDues}
          loading={duesLoading}
          onPayViaStripe={handleUnifiedStripePayment}
          onMarkAsPaid={handleUnifiedMarkAsPaid}
          onDownloadReport={handleUnifiedReportDownload}
          onUpdatePaymentTerms={handleUpdatePaymentTerms}
          markingKey={markingKey}
          downloadingKey={downloadingKey}
          stripePayingKey={stripePayingKey}
          paymentTermsSavingKey={paymentTermsSavingKey}
          activeFilters={activeFilters}
          autopayStates={autopayStates}
          autopayLoading={autopayLoading}
          payerProfile={payerProfile}
          onAutopayChange={applyAutopayState}
        />
      </div>
      <StripePaymentModal
        open={stripeModalState.isOpen}
        clientSecret={stripeModalState.clientSecret}
        paymentIntentId={stripeModalState.paymentIntentId}
        amount={stripeModalState.amount}
        currency={stripeModalState.currency}
        applicationFeeAmount={stripeModalState.applicationFeeAmount}
        netTransferAmount={stripeModalState.netTransferAmount}
        paymentMethodType={stripeModalState.paymentMethodType}
        onCancel={closeStripeModal}
        onSuccess={handleStripePaymentSuccess}
      />
      <PaymentMethodSelectionDialog
        open={Boolean(pendingStripePayment)}
        amount={getPendingPaymentAmount()}
        currency="usd"
        isLoading={creatingStripePayment || processingExtraPayment}
        savedMethod={savedMethodOption}
        onCancel={closePaymentMethodDialog}
        onConfirm={handlePaymentMethodConfirm}
        onConfirmSaved={handleSavedMethodConfirm}
      />
      <ExtraPaymentModal
        isOpen={extraPaymentModalOpen}
        onClose={() => setExtraPaymentModalOpen(false)}
        onConfirm={handleExtraPaymentConfirm}
        isLoading={processingExtraPayment}
      />
    </MainLayout>
  );
}
